import defaultMoment from 'moment';
import {
  alertSeriesUnitMapping,
} from '@components/metrics-panel-hz/hooks/alerts/aiAlertUtils';
import { getMetricUnitText } from '@utils/metrics';
import { TOOLTIP_LABEL_FORMAT, TOOLTIP_LABEL_FORMAT_RT } from '@utils/constants';
import { humanize } from '@utils';
import { DOD_SERIES } from '../utils/dodSeriesOption';
import { METRIC_SERIES } from '../utils/lineChartInputSchema';
import { AI_ALERT_PCP, AiAlertSeriesKeys } from './alerts/constants';

export enum CHART_TYPE {
  LINE = 'line',
  BAR = 'bar',
  DISTRIBUTION = 'distribution',
  DIAGNOSTICS = 'diagnostics',
  BOOLEAN = 'boolean',
}

const SHORT = 30 * 60 * 1000; // short window
const DAY = 24 * 60 * 60 * 1000;
const WEEK = 7 * DAY;
const MONTH = 31 * DAY;

export const getAxisLabelFormat = (diff) => {
  if (diff <= SHORT) {
    return 'HH:mm:ss';
  }
  if (diff <= DAY) {
    return 'HH:mm';
  }
  if (DAY < diff && diff <= WEEK) {
    return 'ddd HH:mm';
  }
  if (WEEK < diff && diff <= MONTH) {
    return 'MMM DD';
  }
  return 'YYYY MMM DD';
};

const showDetailedTooltip = (param) => AiAlertSeriesKeys.includes(param.seriesId);

const formatValue = (value = 'NA', unit) => {
  if (value !== 'NA') {
    return value === null ? 'NA' : `${humanize(value)}${getMetricUnitText(unit)}`;
  }
  return value;
};

const formatAlertValue = (item) => {
  const value = item.value[1];
  const seriesId = item.seriesId;

  if (value === null) {
    return;
  }
  if (value === 'NA') {
    return value;
  }

  const humanizedValue = `${humanize(value)}${alertSeriesUnitMapping[seriesId]}`;

  if (seriesId === AI_ALERT_PCP) {
    const color = value > 0 ? 'red' : 'green';
    return `<span style="margin-right: 10px; color: ${color};">${humanizedValue}</span>`;
  }

  return `<span style="margin-right: 10px;">${humanizedValue}</span>`;
};

const formatDodValue = (item, unit, moment = defaultMoment) => {
  const time = moment(item.value[0]).format(TOOLTIP_LABEL_FORMAT);
  const value = formatValue(item.value[1], unit);
  return { time, value };
  // return `<span>${time}</span><br/><span style="margin-right: 10px;">${value}</span>`;
};

const formatSeriesName = (name, limit = 20) => {
  return name.length > limit ? name.slice(0, limit - 1) + '&hellip;' : name;
};

// extended copy of the formatter in chart utils
export const tooltipFormatter = ({ params, unit, diff, metricInfo, moment = defaultMoment }) => {
  try {
    const yValues: any = [];

    if (Array.isArray(params)) {
      // const detailedTooltip = params.some(showDetailedTooltip);
      const detailedTooltip = params.length > 1;

      params.forEach((item) => {
        const marker = `<span style="
        display: inline-block;
        background-color: ${item.color};
        width: 7px;
        height: 14px;
        position: relative;
        top: 2px;
        "></span>`;

        let extra = '';

        if (detailedTooltip && item.seriesId !== METRIC_SERIES) {
          extra = `<span style="margin: 0px 5px">${formatSeriesName(item.seriesName)}</span>`;
        }

        let time: any = '';
        let value: any = '';

        // special behaviour for ai alert series
        if (item.seriesId in alertSeriesUnitMapping) {
          value = formatAlertValue(item);
        } else if (item.seriesId === DOD_SERIES) {
          ({ time, value } = formatDodValue(item, unit, moment));
        } else {
          const humanizedValue = formatValue(item.value[1], unit);
          value = `<span style="margin-right: 10px;">${humanizedValue}</span>`;
        }

        if (time) {
          yValues.push(`${time} <br/>`);
        }

        if (value) {
          yValues.push(`${marker} ${value} ${extra}<br/>`);
        }
        // if (index % 2 === 1) {
        // yValues.push('<br/>');
        //}
      });
    }

    const labelFormat = diff < SHORT ? TOOLTIP_LABEL_FORMAT_RT : TOOLTIP_LABEL_FORMAT;
    const xVal = moment(params[0].value[0]).format(labelFormat);

    return `<div data-testid="tooltip-${metricInfo.displayName}">${xVal}<br/>${yValues.join('')}</div>`;
  } catch (err) {
    /* pass */
  }

  return `<div data-testid="tooltip-${metricInfo.displayName}">...</div>`;
};

// find the axis index that can be associated with annotation series
export const findAxisIndex = (axis, id) => {
  const axisArr = Array.isArray(axis) ? axis : [axis];
  return axisArr.findIndex((axi) => axi && axi.id === id);
};
